import React, { useState, useCallback } from 'react';
import AsyncSelect from 'react-select/async';

const CustomerSelectionCard = ({
    customers = [],
    selectedCustomer,
    onCustomerSelect,
    onFetchCustomerDetails,
    onCreateCustomer,
    loading = false
}) => {

    const [isLoadingDetails, setIsLoadingDetails] = useState(false);

    // Debug logging
    React.useEffect(() => {
        console.log('CustomerSelectionCard selectedCustomer changed:', selectedCustomer);
    }, [selectedCustomer]);

    // Format customer display based on type
    const getCustomerLabel = useCallback((customer) => {
        if (!customer) return '';

        if (customer.type === 'company') {
            const orgNumber = customer.organization_number || customer.orgnumber;
            return `${customer.name}${orgNumber ? ` (${orgNumber})` : ''}`;
        } else {
            const firstName = customer.first_name || customer.firstName || '';
            const lastName = customer.last_name || customer.lastName || '';
            const fullName = customer.name || `${firstName} ${lastName}`.trim();
            return fullName;
        }
    }, []);

    // Filter customers by search term
    const filterCustomers = useCallback((inputValue) => {
        const searchTerm = inputValue.toLowerCase();
        return customers.filter(customer => {
            const name = (customer.name || '').toLowerCase();
            const orgNumber = (customer.organization_number || customer.orgnumber || '').toLowerCase();
            const customerNumber = (customer.number || '').toString().toLowerCase();
            const email = (customer.email || '').toLowerCase();

            return name.includes(searchTerm) ||
                orgNumber.includes(searchTerm) ||
                customerNumber.includes(searchTerm) ||
                email.includes(searchTerm);
        });
    }, [customers]);

    // Load customer options for the async select
    const loadCustomerOptions = useCallback((inputValue) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const filteredCustomers = filterCustomers(inputValue);
                const options = filteredCustomers.slice(0, 50).map(customer => ({
                    value: customer.id,
                    label: getCustomerLabel(customer),
                    data: customer
                }));
                resolve(options);
            }, 300);
        });
    }, [filterCustomers, getCustomerLabel]);

    // Handle customer selection
    const handleSelectCustomer = useCallback(async (selectedOption) => {
        if (selectedOption) {
            const customer = customers.find(c => c.id === selectedOption.value);
            if (customer && onFetchCustomerDetails) {
                setIsLoadingDetails(true);
                try {
                    const fullCustomer = await onFetchCustomerDetails(customer.id);
                    onCustomerSelect(fullCustomer || customer);
                } catch (error) {
                    console.error('Error fetching customer details:', error);
                    onCustomerSelect(customer);
                } finally {
                    setIsLoadingDetails(false);
                }
            } else {
                onCustomerSelect(customer);
            }
        } else {
            onCustomerSelect(null);
        }
    }, [customers, onFetchCustomerDetails, onCustomerSelect]);

    // Get customer address for display
    const getCustomerAddress = useCallback((customer) => {
        if (!customer?.addresses?.length) return null;
        const address = customer.addresses.find(a => a.address_type === 'billing') || customer.addresses[0];
        return address;
    }, []);

    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '48px',
            borderRadius: '8px',
            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
            borderWidth: '1px',
            boxShadow: state.isFocused
                ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
                : 'none',
            '&:hover': {
                borderColor: '#3b82f6'
            }
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            zIndex: 1000
        }),
        option: (base, { isFocused, isSelected }) => ({
            ...base,
            backgroundColor: isSelected
                ? '#3b82f6'
                : isFocused
                    ? '#f3f4f6'
                    : 'white',
            color: isSelected ? 'white' : '#374151'
        })
    };

    return (
        <>
            <style>{`
                .customer-selection-card {
                    background: white;
                    border-radius: 12px;
                    padding: 2rem;
                    margin-bottom: 1.5rem;
                    border: 1px solid #e5e7eb;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }
                
                .section-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 1.5rem;
                    padding-bottom: 1rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                
                .section-icon {
                    width: 40px;
                    height: 40px;
                    background: #f0f9ff;
                    border: 1px solid #0284c7;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #0284c7;
                    font-size: 1.25rem;
                }
                
                .section-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                }
                
                .section-subtitle {
                    font-size: 0.875rem;
                    color: #64748b;
                    margin: 0.25rem 0 0 0;
                }
                
                .search-section {
                    margin-bottom: 1.5rem;
                }
                
                .form-label-modern {
                    display: block;
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.5rem;
                }
                
                .required-asterisk {
                    color: #dc2626;
                    margin-left: 0.25rem;
                }
                
                .form-text-modern {
                    margin-top: 0.5rem;
                    font-size: 0.75rem;
                    color: #6b7280;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .loading-indicator {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    color: #64748b;
                    font-size: 0.875rem;
                    margin: 1rem 0;
                }
                
                .loading-spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid #e2e8f0;
                    border-top: 2px solid #3b82f6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .actions-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: 1rem;
                }
                
                .btn-add-customer {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    background: #f0f9ff;
                    color: #0369a1;
                    border: 1px solid #0284c7;
                    border-radius: 6px;
                    font-size: 0.875rem;
                    font-weight: 500;
                    text-decoration: none;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                
                .btn-add-customer:hover {
                    background: #e0f2fe;
                    color: #0369a1;
                    border-color: #0369a1;
                    text-decoration: none;
                }
                
                .customer-count {
                    font-size: 0.75rem;
                    color: #6b7280;
                    font-weight: 500;
                }
                
                /* Selected Customer Section - Integrated, no nested card appearance */
                .selected-customer-section {
                    margin-top: 2rem;
                    padding-top: 1.5rem;
                    border-top: 1px solid #e5e7eb;
                }
                
                .customer-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 1.5rem;
                }
                
                .customer-info {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                }
                
                .customer-avatar {
                    width: 48px;
                    height: 48px;
                    border-radius: 8px;
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #6c757d;
                    font-size: 1.25rem;
                    flex-shrink: 0;
                }
                
                .customer-details h3 {
                    font-size: 1.125rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 0.5rem 0;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .success-icon {
                    width: 16px;
                    height: 16px;
                    background: #16a34a;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 0.625rem;
                }
                
                .customer-type-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.25rem 0.75rem;
                    background: #f8f9fa;
                    color: #6c757d;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                
                .btn-remove {
                    padding: 0.5rem;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                
                .btn-remove:hover {
                    background: #fee2e2;
                    border-color: #fca5a5;
                    color: #dc2626;
                }
                
                .company-info {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                    padding-bottom: 1.5rem;
                    margin-bottom: 1.5rem;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }
                
                .info-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #6b7280;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }
                
                .info-value {
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: #1e293b;
                }
                
                .contact-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 1.5rem;
                }
                
                .contact-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                }
                
                .contact-icon {
                    width: 32px;
                    height: 32px;
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #6c757d;
                    font-size: 0.875rem;
                    flex-shrink: 0;
                    margin-top: 0.125rem;
                }
                
                .contact-details {
                    min-width: 0;
                    flex: 1;
                }
                
                .contact-details .info-label {
                    margin-bottom: 0.25rem;
                }
                
                .contact-details .info-value {
                    color: #3b82f6;
                    text-decoration: none;
                    word-break: break-word;
                }
                
                .contact-details .info-value:hover {
                    color: #1d4ed8;
                    text-decoration: underline;
                }
                
                .address-text {
                    line-height: 1.4;
                }
                
                .customer-footer {
                    margin-top: 1.5rem;
                    padding-top: 1rem;
                    border-top: 1px solid #f1f5f9;
                }
                
                .customer-number {
                    font-size: 0.75rem;
                    color: #6b7280;
                    font-weight: 500;
                }
                
                .customer-number strong {
                    color: #1e293b;
                    font-weight: 600;
                }
                
                @media (max-width: 768px) {
                    .customer-selection-card {
                        padding: 1.5rem;
                    }
                    
                    .customer-header {
                        flex-direction: column;
                        gap: 1rem;
                        align-items: stretch;
                    }
                    
                    .actions-row {
                        flex-direction: column;
                        gap: 0.75rem;
                        align-items: stretch;
                    }
                    
                    .contact-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .company-info {
                        grid-template-columns: 1fr;
                    }
                    
                    .section-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.5rem;
                    }
                }
            `}</style>

            <div className="customer-selection-card">
                {/* Section Header - Same as other sections */}
                <div className="section-header">
                    <div className="section-icon">
                        <i className="bi bi-person"></i>
                    </div>
                    <div>
                        <h2 className="section-title">Kund</h2>
                        <p className="section-subtitle">
                            {selectedCustomer
                                ? `${selectedCustomer.name} är vald`
                                : 'Välj kund för fakturan'
                            }
                        </p>
                    </div>
                </div>

                {/* Search Section */}
                <div className="search-section">
                    <label className="form-label-modern">
                        {selectedCustomer ? 'Byt kund' : 'Sök och välj kund'}
                        <span className="required-asterisk">*</span>
                    </label>
                    <AsyncSelect
                        cacheOptions
                        defaultOptions={customers.slice(0, 20).map(customer => ({
                            value: customer.id,
                            label: getCustomerLabel(customer),
                            data: customer
                        }))}
                        value={(() => {
                            const value = selectedCustomer ? {
                                value: selectedCustomer.id,
                                label: getCustomerLabel(selectedCustomer)
                            } : null;
                            console.log('AsyncSelect value:', value, 'selectedCustomer:', selectedCustomer);
                            return value;
                        })()}
                        loadOptions={loadCustomerOptions}
                        onChange={handleSelectCustomer}
                        isClearable
                        placeholder={selectedCustomer ? "Sök för att byta kund..." : "Börja skriv för att söka kund..."}
                        noOptionsMessage={() => "Inga kunder hittades"}
                        loadingMessage={() => "Söker kunder..."}
                        isDisabled={loading || isLoadingDetails}
                        classNamePrefix="react-select"
                        styles={customSelectStyles}
                    />
                    <div className="form-text-modern">
                        <span>
                            {selectedCustomer
                                ? 'Sök med namn, organisationsnummer eller e-post för att byta kund'
                                : 'Sök med namn, organisationsnummer, kundnummer eller e-post'
                            }
                        </span>
                    </div>
                </div>

                {isLoadingDetails && (
                    <div className="loading-indicator">
                        <div className="loading-spinner"></div>
                        <span>Hämtar kunduppgifter...</span>
                    </div>
                )}

                <div className="actions-row">
                    <button
                        type="button"
                        className="btn-add-customer"
                        onClick={() => {
                            if (onCreateCustomer) {
                                onCreateCustomer();
                            } else {
                                console.log('Create new customer');
                            }
                        }}
                    >
                        <i className="bi bi-plus-lg"></i>
                        Skapa ny kund
                    </button>

                    <span className="customer-count">
                        {customers.length} kunder tillgängliga
                    </span>
                </div>

                {/* Selected Customer Display - Integrated into main card */}
                {selectedCustomer && (
                    <div className="selected-customer-section">
                        {/* Header */}
                        <div className="customer-header">
                            <div className="customer-info">
                                <div className="customer-avatar">
                                    <i className={`bi bi-${selectedCustomer.type === 'company' ? 'building' : 'person-fill'}`}></i>
                                </div>
                                <div className="customer-details">
                                    <h3>
                                        {selectedCustomer.name}
                                        <div className="success-icon">
                                            <i className="bi bi-check"></i>
                                        </div>
                                    </h3>
                                    <span className="customer-type-badge">
                                        <i className={`bi bi-${selectedCustomer.type === 'company' ? 'building' : 'person'}`}></i>
                                        {selectedCustomer.type === 'company' ? 'Företag' : 'Privatperson'}
                                    </span>
                                </div>
                            </div>

                            <button
                                className="btn-remove"
                                onClick={() => onCustomerSelect(null)}
                                title="Ta bort vald kund"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        {/* Company Info */}
                        {selectedCustomer.type === 'company' && selectedCustomer.orgnumber && (
                            <div className="company-info">
                                <div className="info-item">
                                    <div className="info-label">Organisationsnummer</div>
                                    <div className="info-value">{selectedCustomer.orgnumber}</div>
                                </div>
                                {selectedCustomer.vatnumber && (
                                    <div className="info-item">
                                        <div className="info-label">VAT-nummer</div>
                                        <div className="info-value">{selectedCustomer.vatnumber}</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Contact Information */}
                        <div className="contact-grid">
                            {selectedCustomer.email && (
                                <div className="contact-item">
                                    <div className="contact-icon">
                                        <i className="bi bi-envelope"></i>
                                    </div>
                                    <div className="contact-details">
                                        <div className="info-label">E-post</div>
                                        <a
                                            href={`mailto:${selectedCustomer.email}`}
                                            className="info-value"
                                        >
                                            {selectedCustomer.email}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {selectedCustomer.phone && (
                                <div className="contact-item">
                                    <div className="contact-icon">
                                        <i className="bi bi-telephone"></i>
                                    </div>
                                    <div className="contact-details">
                                        <div className="info-label">Telefon</div>
                                        <a
                                            href={`tel:${selectedCustomer.phone}`}
                                            className="info-value"
                                        >
                                            {selectedCustomer.phone}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {getCustomerAddress(selectedCustomer) && (
                                <div className="contact-item">
                                    <div className="contact-icon">
                                        <i className="bi bi-geo-alt"></i>
                                    </div>
                                    <div className="contact-details">
                                        <div className="info-label">Faktureringsadress</div>
                                        <div className="info-value address-text">
                                            {(() => {
                                                const addr = getCustomerAddress(selectedCustomer);
                                                return (
                                                    <>
                                                        {addr.street1 && <div>{addr.street1}</div>}
                                                        {addr.street2 && <div>{addr.street2}</div>}
                                                        <div>
                                                            {addr.postal_code && `${addr.postal_code} `}
                                                            {addr.city}
                                                        </div>
                                                        {addr.country && addr.country !== 'Sverige' && (
                                                            <div>{addr.country}</div>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {(selectedCustomer.customer_number || selectedCustomer.number) && (
                            <div className="customer-footer">
                                <div className="customer-number">
                                    Kundnummer: <strong>{selectedCustomer.customer_number || selectedCustomer.number}</strong>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default CustomerSelectionCard;